import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import {
  netsuiteConfigQueryOptions,
  netsuiteSigningKeyQueryOptions,
  updateNetsuiteConfig,
  type NetsuiteConfig,
} from '../../lib/queries/customers';

const EMPTY: NetsuiteConfig = {
  downstreamType: 'mock',
  restletUrl: '',
  tokenEndpoint: '',
  oauthClientId: '',
  oauthCertId: '',
  oauthScope: '',
  nativeMcpUrl: '',
};

const selectCls =
  'w-full appearance-none rounded border border-white/10 bg-white/4 px-3 py-2 pr-9 text-sm text-primary focus:border-[--color-accent] focus:outline-none';

export default function NetSuiteRouting({ id }: { id: string }) {
  const qc = useQueryClient();
  const { data } = useQuery(netsuiteConfigQueryOptions(id));
  const { data: signingKey } = useQuery(netsuiteSigningKeyQueryOptions(id));
  const [form, setForm] = useState<NetsuiteConfig>(EMPTY);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: () => updateNetsuiteConfig(id, form),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['netsuite-config', id] }),
  });

  const set =
    (k: keyof NetsuiteConfig) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const isRestlet = form.downstreamType === 'restlet';
  const isNative = form.downstreamType === 'native_mcp';

  const missing =
    (isRestlet &&
      (!form.restletUrl ||
        !form.tokenEndpoint ||
        !form.oauthClientId ||
        !form.oauthCertId)) ||
    (isNative && !form.nativeMcpUrl);

  // Only warn once the key status has loaded and is confirmed absent — avoids
  // a warning flash on first paint.
  const needsKey = isRestlet && signingKey?.provisioned === false;

  return (
    <div className="bg-bg-elevated border-primary/8 rounded-lg border p-5">
      <span className="text-subtle text-xs font-semibold tracking-widest uppercase">
        Downstream Routing
      </span>

      <div className="mt-4 space-y-4">
        <Field label="Adapter (downstream_type)">
          <div className="relative">
            <select
              value={form.downstreamType}
              onChange={set('downstreamType')}
              className={selectCls}
            >
              <option value="mock" className="bg-bg-elevated text-primary">
                mock — fixtures
              </option>
              <option value="restlet" className="bg-bg-elevated text-primary">
                restlet — SuiteScript RESTlet
              </option>
              <option
                value="native_mcp"
                className="bg-bg-elevated text-primary"
              >
                native_mcp — tenant MCP server
              </option>
            </select>
            <ChevronDown
              size={16}
              className="text-muted pointer-events-none absolute top-1/2 right-3 -translate-y-1/2"
            />
          </div>
        </Field>

        {isRestlet && (
          <>
            {needsKey && (
              <div className="flex items-start gap-2 rounded border border-yellow-500/30 bg-yellow-500/5 p-3">
                <AlertTriangle
                  size={14}
                  className="mt-0.5 shrink-0 text-yellow-400"
                />
                <p className="text-xs text-yellow-200">
                  No signing key is provisioned for this tenant. The RESTlet
                  adapter can't authenticate until you upload the RSA private
                  key in the “NetSuite Signing Key” panel above.
                </p>
              </div>
            )}
            <Field label="restlet_url">
              <input
                value={form.restletUrl}
                onChange={set('restletUrl')}
                placeholder="https://<acct>.restlets.api.netsuite.com/..."
                className={inputCls}
              />
            </Field>
            <Field label="token_endpoint">
              <input
                value={form.tokenEndpoint}
                onChange={set('tokenEndpoint')}
                placeholder="https://<acct>.suitetalk.api.netsuite.com/.../token"
                className={inputCls}
              />
            </Field>
            <Field label="oauth_client_id">
              <input
                value={form.oauthClientId}
                onChange={set('oauthClientId')}
                className={inputCls}
              />
            </Field>
            <Field label="oauth_cert_id">
              <input
                value={form.oauthCertId}
                onChange={set('oauthCertId')}
                className={inputCls}
              />
            </Field>
            <Field label="oauth_scope (optional)">
              <input
                value={form.oauthScope}
                onChange={set('oauthScope')}
                placeholder="restlets rest_webservices"
                className={inputCls}
              />
            </Field>
            <p className="text-subtle text-[0.625rem]">
              The RSA private key is provisioned separately in the “NetSuite
              Signing Key” panel above.
            </p>
          </>
        )}

        {isNative && (
          <Field label="native_mcp_url">
            <input
              value={form.nativeMcpUrl}
              onChange={set('nativeMcpUrl')}
              placeholder="https://<tenant-mcp-host>/mcp"
              className={inputCls}
            />
          </Field>
        )}

        <div className="flex items-center justify-end gap-3 pt-1">
          {mutation.isError && (
            <p className="text-danger mr-auto text-xs">
              {(mutation.error as Error).message}
            </p>
          )}
          {mutation.isSuccess && (
            <p className="text-success mr-auto text-xs">Saved</p>
          )}
          <button
            onClick={() => mutation.mutate()}
            disabled={missing || mutation.isPending}
            className="bg-accent rounded px-3 py-1 text-xs text-white disabled:opacity-40"
          >
            {mutation.isPending ? 'Saving…' : 'Save routing'}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  'w-full rounded border border-white/10 bg-white/4 px-3 py-2 text-sm text-primary placeholder:text-primary/20 focus:border-[--color-accent] focus:outline-none';

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-muted mb-1 block text-xs font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}
